import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { toNum, fmtInput } from "../lib/api";
import { playThunder } from "../lib/thunder";

export function LightningRequestDialog({ clientId, open, onClose, onSent, defaultAddress = "" }) {
  const [address, setAddress] = useState(defaultAddress);
  const [offer, setOffer] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setAddress(defaultAddress);
      setOffer("");
    }
  }, [open, defaultAddress]);

  const submit = async () => {
    if (!address.trim() || !toNum(offer)) {
      toast.error("Add the property address and offer amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/lightning-requests`, { address, offer_price: toNum(offer) });
      playThunder();
      toast.success("\u26a1 Lightning request sent! Your lender is on it.");
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error("Could not send the lightning request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="bg-[#0b0b0f]/95 backdrop-blur-xl border-[#FFD23F]/30 text-white max-w-md" data-testid="lightning-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#FFD23F]" /> Lightning request
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Don't have time to build your own approval? No problem — your lender will do it for you in lightning time. Just drop the address and offer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Property address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Palm Ave, Miami, FL"
              className="mt-2 bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#FFD23F]" data-testid="lightning-address" />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-white/50">Offer / purchase price</Label>
            <Input value={offer} onChange={(e) => setOffer(fmtInput(e.target.value))} inputMode="numeric" placeholder="$650,000"
              className="mt-2 bg-black/40 border-white/15 h-11 text-white focus:ring-2 focus:ring-[#FFD23F]" data-testid="lightning-offer" />
            <p className="text-[11px] text-white/40 mt-1.5">Your lender will look up the taxes, HOA and everything else for you.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="h-11 border-white/20 bg-transparent text-white hover:bg-white/10 rounded-xl px-5">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="lightning-send"
            className="h-11 rounded-xl px-6 font-extrabold text-black bg-gradient-to-r from-[#FFD23F] to-[#FF8A00] hover:brightness-110 shadow-[0_0_24px_rgba(255,210,63,0.5)]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Zap className="h-4 w-4 mr-1.5" /> Send lightning request</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
